// No legacy SegmentTypes imports needed — mail service has its own types below.

// Use empty string for dev (Vite proxy handles it), or full URL for production
const API_URL = import.meta.env.PROD ? (import.meta.env.VITE_API_URL || '') : ''

interface APIResponse<T> {
  ok: boolean
  data?: T
  error?: { code: string; message: string }
}

export async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<APIResponse<T>> {
  const token = localStorage.getItem('access_token')

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    credentials: 'include',
  })

  const body = await response.json()

  // Handle both wrapped ({ok, data}) and unwrapped responses. Body may also be
  // null (e.g. endpoints returning an optional model as JSON null) or a
  // primitive; guard with typeof check so `'ok' in body` doesn't throw.
  if (response.ok) {
    const isWrapped =
      body !== null &&
      typeof body === 'object' &&
      !Array.isArray(body) &&
      'ok' in body &&
      'data' in body
    if (isWrapped) {
      return body
    }
    return { ok: true, data: body }
  }

  // Error response
  return {
    ok: false,
    error: {
      code: String(response.status),
      message: extractErrorMessage(body),
    },
  }
}

// FastAPI error bodies are not uniform: `detail` may be a string (HTTPException),
// an array of validation objects (422: [{loc, msg, type, input}]), or a dict
// (our KNOWN_ERRORS: {err: "..."}). Always collapse to a string so callers can
// safely render it — an object/array reaches React as a child and crashes (#31).
function extractErrorMessage(body: unknown): string {
  if (!body || typeof body !== 'object') return 'Request failed'
  const b = body as Record<string, unknown>
  const detail = b.detail

  if (Array.isArray(detail)) {
    const parts = detail.map((d) => {
      const item = d as Record<string, unknown>
      const loc = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : undefined
      const msg = typeof item?.msg === 'string' ? item.msg : 'invalid'
      return loc ? `${loc}: ${msg}` : msg
    })
    return parts.join('; ') || 'Request failed'
  }
  if (typeof detail === 'string') return detail
  if (detail && typeof detail === 'object') {
    const d = detail as Record<string, unknown>
    if (typeof d.err === 'string') return d.err
    if (typeof d.message === 'string') return d.message
    return JSON.stringify(detail)
  }
  if (typeof b.message === 'string') return b.message
  return 'Request failed'
}

export async function getPermissions() {
  return fetchWithAuth<{
    user_id: string
    email: string
    role: string
    permissions: Record<string, string[]>
  }>('/admin/permissions/me')
}

// Listing types
export interface ListingSummary {
  id: string
  title: string
  price: number
  currency: string
  status: 'pending' | 'approved' | 'rejected' | 'removed' | 'deleted'
  seller_id: string
  seller_name: string
  category: string
  created_at: string
  updated_at: string
  image_url: string | null
}

export interface ListingDetail extends ListingSummary {
  description: string
  seller_email: string
  subcategory: string | null
  condition: string | null
  location: string | null
  images: string[]
  moderation_notes: string | null
  moderated_by: string | null
  moderated_at: string | null
}

export interface ListingListResponse {
  items: ListingSummary[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface ListingFilters {
  status?: string
  seller_id?: string
  category?: string
  search?: string
  date_from?: string
  date_to?: string
  page?: number
  page_size?: number
}

// Listings API
export async function getListings(filters: ListingFilters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.append(key, String(value))
    }
  })
  return fetchWithAuth<ListingListResponse>(`/admin/listings?${params}`)
}

export async function getListing(id: string) {
  return fetchWithAuth<ListingDetail>(`/admin/listings/${id}`)
}

export async function updateListing(id: string, data: Partial<ListingDetail>) {
  return fetchWithAuth<ListingDetail>(`/admin/listings/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function approveListing(id: string, notes?: string) {
  return fetchWithAuth<ListingDetail>(`/admin/listings/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  })
}

export async function rejectListing(id: string, reason: string, notes?: string) {
  return fetchWithAuth<ListingDetail>(`/admin/listings/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason, notes }),
  })
}

export async function removeListing(id: string, reason: string) {
  return fetchWithAuth<ListingDetail>(`/admin/listings/${id}/remove`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export async function deleteListing(id: string, reason: string) {
  return fetchWithAuth<{ success: boolean }>(`/admin/listings/${id}?reason=${encodeURIComponent(reason)}`, {
    method: 'DELETE',
  })
}

export async function bulkListingAction(listingIds: string[], action: string, reason?: string) {
  return fetchWithAuth<{ success: boolean; processed: number; errors: number }>('/admin/listings/bulk', {
    method: 'POST',
    body: JSON.stringify({ listing_ids: listingIds, action, reason }),
  })
}

// User types
export interface UserSummary {
  id: string
  email: string
  name: string | null
  status: 'active' | 'suspended' | 'banned' | 'deleted'
  created_at: string
  last_active_at: string | null
  listing_count: number
  shop_id: string | null
}

export interface UserDetail extends UserSummary {
  phone: string | null
  avatar_url: string | null
  updated_at: string
  email_verified: boolean
  phone_verified: boolean
  transaction_count: number
  shop_name: string | null
  admin_role: string | null
  moderation_notes: string | null
  moderated_by: string | null
  moderated_at: string | null
}

export interface UserListResponse {
  items: UserSummary[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface UserFilters {
  status?: string
  search?: string
  has_shop?: boolean
  date_from?: string
  date_to?: string
  page?: number
  page_size?: number
}

// Users API
export async function getUsers(filters: UserFilters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.append(key, String(value))
    }
  })
  return fetchWithAuth<UserListResponse>(`/admin/users?${params}`)
}

export async function getUser(id: string) {
  return fetchWithAuth<UserDetail>(`/admin/users/${id}`)
}

export async function updateUser(id: string, data: { email?: string; phone?: string; name?: string }) {
  return fetchWithAuth<UserDetail>(`/admin/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function suspendUser(id: string, reason: string, notes?: string) {
  return fetchWithAuth<UserDetail>(`/admin/users/${id}/suspend`, {
    method: 'POST',
    body: JSON.stringify({ reason, notes }),
  })
}

export async function unsuspendUser(id: string, notes?: string) {
  return fetchWithAuth<UserDetail>(`/admin/users/${id}/unsuspend`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  })
}

export async function banUser(id: string, reason: string) {
  return fetchWithAuth<UserDetail>(`/admin/users/${id}/ban`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export async function deleteUser(id: string, reason: string) {
  return fetchWithAuth<{ success: boolean }>(`/admin/users/${id}?reason=${encodeURIComponent(reason)}`, {
    method: 'DELETE',
  })
}

export async function exportUserData(id: string) {
  return fetchWithAuth<{ id: string; email: string; listings: unknown[]; transactions: unknown[] }>(
    `/admin/users/${id}/export`
  )
}

// Shop types
export interface ShopSummary {
  id: string
  name: string
  slug_token: string | null
  owner_id: string
  owner_name: string | null
  owner_email: string
  status: 'pending' | 'verified' | 'suspended' | 'closed'
  listing_count: number
  created_at: string
  verified_at: string | null
}

export interface ShopDetail extends ShopSummary {
  description: string | null
  owner_phone: string | null
  logo_url: string | null
  banner_url: string | null
  location: string | null
  total_sales: number
  updated_at: string
  verified_by: string | null
  moderation_notes: string | null
  moderated_by: string | null
  moderated_at: string | null
}

export interface ShopListResponse {
  items: ShopSummary[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface ShopFilters {
  status?: string
  search?: string
  date_from?: string
  date_to?: string
  page?: number
  page_size?: number
}

// Shops API
export async function getShops(filters: ShopFilters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.append(key, String(value))
    }
  })
  return fetchWithAuth<ShopListResponse>(`/admin/shops?${params}`)
}

export async function getShop(id: string) {
  return fetchWithAuth<ShopDetail>(`/admin/shops/${id}`)
}

export async function verifyShop(id: string, notes?: string) {
  return fetchWithAuth<ShopDetail>(`/admin/shops/${id}/verify`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  })
}

export async function suspendShop(id: string, reason: string) {
  return fetchWithAuth<ShopDetail>(`/admin/shops/${id}/suspend`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export async function unsuspendShop(id: string, notes?: string) {
  return fetchWithAuth<ShopDetail>(`/admin/shops/${id}/unsuspend`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  })
}

export async function closeShop(id: string, reason: string) {
  return fetchWithAuth<ShopDetail>(`/admin/shops/${id}/close`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

// Impersonation
export interface ImpersonationLink {
  method: 'link'
  action_link: string
  owner_email: string
  otp: string
  expires_in: number
  shop_id: string
  shop_name: string
}

export async function impersonateShopOwner(shopId: string) {
  return fetchWithAuth<ImpersonationLink>(`/admin/shops/${shopId}/impersonate`, {
    method: 'POST',
  })
}

// ---------------------------------------------------------------------------
// Mail Service — audience filter language
// ---------------------------------------------------------------------------

export type AudienceField =
  | 'signup_date'
  | 'last_seen'
  | 'shop_tier'
  | 'is_seller'
  | 'country'
  | 'has_purchased'
  | 'email_verified'
  | 'user_id'

export type AudienceOp =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'in'
  | 'nin'
  | 'contains'

export interface AudienceCond {
  kind: 'cond'
  field: AudienceField
  op: AudienceOp
  value: string | number | boolean | (string | number)[]
}

export interface AudienceAnd {
  kind: 'and'
  children: AudienceFilter[]
}

export interface AudienceOr {
  kind: 'or'
  children: AudienceFilter[]
}

export interface AudienceNot {
  kind: 'not'
  child: AudienceFilter
}

export type AudienceFilter = AudienceCond | AudienceAnd | AudienceOr | AudienceNot

/** XOR: exactly one of preset_key, custom_filter, or external_list_ids. */
export interface AudienceResolveRequest {
  preset_key?: string | null
  custom_filter?: AudienceFilter | null
  external_list_ids?: number[]
}

export interface AudiencePreset {
  key: string
  label: string
  description?: string | null
  estimated_size_hint?: number | null
}

export interface AudiencePresetList {
  presets: AudiencePreset[]
}

export interface EmailRecipient {
  email: string
  name?: string | null
  user_id?: string | null
  attribs: Record<string, string | number | boolean>
}

/** A user found by GET /admin/mail/audiences/users, for a specific-users audience. */
export interface AudienceUserMatch {
  user_id: string
  email: string
  name?: string | null
  shop_id?: string | null
  shop_name?: string | null
  /** False when the user opted out of marketing email — the send skips them. */
  broadcast_consent: boolean
  /** True when the address hard-bounced — the send skips them. */
  hard_bounced: boolean
}

export interface AudienceResolveResult {
  count: number
  recipients: EmailRecipient[]
  sampled: boolean
  resolved_at: string
}

// ---------------------------------------------------------------------------
// Mail Service — templates
// ---------------------------------------------------------------------------

export interface MailTemplate {
  id: number
  name: string
  is_default: boolean
}

export interface MailTemplateList {
  templates: MailTemplate[]
}

// ---------------------------------------------------------------------------
// Mail Service — campaigns
// ---------------------------------------------------------------------------

export type MailCampaignStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled' | 'failed'

// service_notice ignores marketing opt-outs; only super admins can create or send one.
export type MailCampaignKind = 'marketing' | 'event_blast' | 'service_notice'
export type AdminMailCampaignKind = Exclude<MailCampaignKind, 'event_blast'>

export interface MailCampaignRead {
  id: number
  name: string
  subject: string
  from_email: string
  template_id: number
  body: string
  // 'richtext' or 'html' open in the admin editor; other Listmonk editors own their content.
  content_type: string
  status: MailCampaignStatus
  kind: MailCampaignKind
  audience_summary: string
  recipient_count: number
  scheduled_at: string | null
  started_at: string | null
  completed_at: string | null
  created_by_admin_id: string | null
  listmonk_admin_url: string
}

export interface MailCampaignList {
  items: MailCampaignRead[]
  total: number
  page: number
  page_size: number
}

export interface MailCampaignCreate {
  name: string
  subject: string
  from_email?: string | null
  template_id: number
  body?: string
  audience: AudienceResolveRequest
  scheduled_at?: string | null
  kind?: AdminMailCampaignKind
}

export interface MailCampaignUpdate {
  name?: string | null
  subject?: string | null
  from_email?: string | null
  template_id?: number | null
  body?: string | null
  audience?: AudienceResolveRequest | null
  scheduled_at?: string | null
}

export interface MailCampaignSendRequest {
  // Required by the backend: Listmonk doesn't persist the audience, so it must
  // be re-supplied at send time (and re-resolved for freshness).
  audience: AudienceResolveRequest
  // Must equal the count the audience resolves to when the send starts.
  confirmation_count: number
}

export interface MailCampaignTestSendRequest {
  test_emails: string[]
}

export interface MailCampaignStats {
  campaign_id: number
  recipient_count: number
  delivered: number
  opened: number
  clicked: number
  bounced: number
  unsubscribed: number
  spam_complaints: number
  last_updated_at: string
}

// ---------------------------------------------------------------------------
// Mail Service API calls
// ---------------------------------------------------------------------------

/** GET /admin/mail/audiences/presets */
export async function getMailAudiencePresets() {
  return fetchWithAuth<AudiencePresetList>('/admin/mail/audiences/presets')
}

/** GET /admin/mail/audiences/users — match by email, name, shop name, shop id or user id */
export async function searchMailAudienceUsers(q: string) {
  return fetchWithAuth<{ users: AudienceUserMatch[] }>(
    `/admin/mail/audiences/users?q=${encodeURIComponent(q)}`
  )
}

/** POST /admin/mail/audiences/resolve */
export async function resolveMailAudience(
  body: AudienceResolveRequest,
  kind: AdminMailCampaignKind = 'marketing'
) {
  return fetchWithAuth<AudienceResolveResult>(`/admin/mail/audiences/resolve?kind=${kind}`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/** GET /admin/mail/templates */
export async function getMailTemplates() {
  return fetchWithAuth<MailTemplateList>('/admin/mail/templates')
}

/** GET /admin/mail/campaigns */
export async function getMailCampaigns(filters: {
  status?: MailCampaignStatus
  limit?: number
  offset?: number
} = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.limit !== undefined) params.set('limit', String(filters.limit))
  if (filters.offset !== undefined) params.set('offset', String(filters.offset))
  const qs = params.toString()
  return fetchWithAuth<MailCampaignList>(`/admin/mail/campaigns${qs ? `?${qs}` : ''}`)
}

/** POST /admin/mail/campaigns */
export async function createMailCampaign(data: MailCampaignCreate) {
  return fetchWithAuth<MailCampaignRead>('/admin/mail/campaigns', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/** GET /admin/mail/campaigns/{id} */
export async function getMailCampaign(id: number) {
  return fetchWithAuth<MailCampaignRead>(`/admin/mail/campaigns/${id}`)
}

/** PATCH /admin/mail/campaigns/{id} */
export async function updateMailCampaign(id: number, data: MailCampaignUpdate) {
  return fetchWithAuth<MailCampaignRead>(`/admin/mail/campaigns/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

/** DELETE /admin/mail/campaigns/{id} */
export async function deleteMailCampaign(id: number) {
  return fetchWithAuth<{ ok: boolean }>(`/admin/mail/campaigns/${id}`, { method: 'DELETE' })
}

/** POST /admin/mail/campaigns/{id}/send */
export async function sendMailCampaign(id: number, body: MailCampaignSendRequest) {
  return fetchWithAuth<MailCampaignRead>(`/admin/mail/campaigns/${id}/send`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/** POST /admin/mail/campaigns/{id}/test-send */
export async function testSendMailCampaign(id: number, body: MailCampaignTestSendRequest) {
  return fetchWithAuth<{ ok: boolean }>(`/admin/mail/campaigns/${id}/test-send`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/** POST /admin/mail/campaigns/{id}/preview — omit body to render the saved draft */
export async function previewMailCampaign(
  id: number,
  body: { body?: string; template_id?: number | null } = {}
) {
  return fetchWithAuth<{ html: string }>(`/admin/mail/campaigns/${id}/preview`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/** GET /admin/mail/campaigns/{id}/stats */
export async function getMailCampaignStats(id: number) {
  return fetchWithAuth<MailCampaignStats>(`/admin/mail/campaigns/${id}/stats`)
}

// ---------------------------------------------------------------------------
// Legacy campaign types (kept for reference; old /admin/campaigns/* endpoints
// are superseded by /admin/mail/* — do not use for new code)
// ---------------------------------------------------------------------------

/** @deprecated Use MailCampaignStatus */
export type CampaignStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled' | 'failed'
/** @deprecated */
export type CampaignAudience = 'all_users' | 'active_users' | 'sellers' | 'inactive_users' | 'custom'
/** @deprecated */
export type StreamType = 'broadcast' | 'transactional'

// ---------------------------------------------------------------------------
// Postmark templates + sender signatures (used by Invites / SendInvitesModal)
// These hit the OLD /admin/campaigns/* surface which proxies to Postmark.
// ---------------------------------------------------------------------------

export interface TemplateInfo {
  alias: string
  name: string
  subject: string
}

export interface TemplateDetail extends TemplateInfo {
  html_body: string
  text_body: string
  template_model: Record<string, unknown> | null
}

export interface SenderSignature {
  email: string
  name: string
  domain: string
  reply_to: string | null
}

export async function getTemplates() {
  return fetchWithAuth<TemplateInfo[]>('/admin/campaigns/templates')
}

export async function getTemplate(templateKey: string) {
  return fetchWithAuth<TemplateDetail>(`/admin/campaigns/templates/${templateKey}`)
}

export async function getSenderSignatures() {
  return fetchWithAuth<{ signatures: SenderSignature[] }>('/admin/campaigns/sender-signatures')
}

// Report types
export interface ReportSummary {
  id: string
  type: string
  status: 'pending' | 'investigating' | 'resolved' | 'dismissed'
  reason: string
  reporter_id: string
  reporter_email: string | null
  target_id: string
  target_type: string
  created_at: string
}

export interface ReportDetail extends ReportSummary {
  description: string | null
  target_title: string | null
  updated_at: string
  assigned_to: string | null
  resolution_notes: string | null
  resolved_by: string | null
  resolved_at: string | null
}

export interface ReportListResponse {
  items: ReportSummary[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

// Reports API
export async function getReports(filters: { status?: string; type?: string; page?: number } = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.append(key, String(value))
  })
  return fetchWithAuth<ReportListResponse>(`/admin/reports?${params}`)
}

export async function getReport(id: string) {
  return fetchWithAuth<ReportDetail>(`/admin/reports/${id}`)
}

export async function assignReport(id: string) {
  return fetchWithAuth<ReportDetail>(`/admin/reports/${id}/assign`, { method: 'POST' })
}

export async function resolveReport(id: string, status: string, notes: string) {
  return fetchWithAuth<ReportDetail>(`/admin/reports/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ status, notes }),
  })
}

// Admin User types
export interface AdminUserSummary {
  id: string
  email: string
  name: string | null
  role: string
  created_at: string
  last_active_at: string | null
}

export interface AdminUserListResponse {
  items: AdminUserSummary[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

// Admin Users API
export async function getAdminUsers(filters: { role?: string; search?: string; page?: number } = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.append(key, String(value))
  })
  return fetchWithAuth<AdminUserListResponse>(`/admin/admin-users?${params}`)
}

export async function createAdminUser(email: string, role: string) {
  return fetchWithAuth<AdminUserSummary>('/admin/admin-users', {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  })
}

export async function updateAdminUser(id: string, role: string) {
  return fetchWithAuth<AdminUserSummary>(`/admin/admin-users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  })
}

export async function removeAdminUser(id: string) {
  return fetchWithAuth<{ success: boolean }>(`/admin/admin-users/${id}`, { method: 'DELETE' })
}

// Invite types
export type InviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked'

export interface InviteSummary {
  id: string
  email: string | null
  status: InviteStatus
  created_at: string
  expires_at: string
  accepted_at: string | null
  email_sent_at: string | null
}

export interface InviteDetail extends InviteSummary {
  invite_type: string
  accepted_by: string | null
  revoked_at: string | null
  metadata: Record<string, unknown> | null
}

export interface InviteListResponse {
  items: InviteSummary[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface InviteFilters {
  status?: string
  search?: string
  page?: number
  page_size?: number
}

export interface InviteCreateData {
  emails?: string[]
  count?: number
  expires_in_days?: number
  metadata?: Record<string, unknown>
}

export interface InviteCreateResponse {
  invites: { id: string; token: string; email: string | null; expires_at: string }[]
  count: number
}

export interface InviteSendData {
  invite_ids: string[]
  template_key: string
  base_url: string
  from_email?: string
  subject?: string
  template_vars?: Record<string, string>
}

// Invites API
export async function getInvites(filters: InviteFilters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.append(key, String(value))
  })
  return fetchWithAuth<InviteListResponse>(`/admin/invites?${params}`)
}

export async function getInvite(id: string) {
  return fetchWithAuth<InviteDetail>(`/admin/invites/${id}`)
}

export async function createInvites(data: InviteCreateData) {
  return fetchWithAuth<InviteCreateResponse>('/admin/invites', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function revokeInvite(id: string) {
  return fetchWithAuth<InviteDetail>(`/admin/invites/${id}/revoke`, { method: 'POST' })
}

export async function sendInviteEmails(data: InviteSendData) {
  return fetchWithAuth<{ sent: string[]; failed: { email: string; error: string }[]; skipped: { id: string; reason: string }[] }>(
    '/admin/invites/send',
    { method: 'POST', body: JSON.stringify(data) }
  )
}

// Discover Events types
export interface EventSummary {
  id: string
  name: string
  source: string
  status: string
  categories: string[]
  starts_at: string
  city: string | null
  state: string | null
  image_url: string | null
  created_at: string
}

export interface EventDetail extends EventSummary {
  description: string | null
  url: string | null
  venue_name: string | null
  month_key: string
  source_id: string
  dedupe_key: string
  ends_at: string | null
  price_min_cents: number | null
  price_max_cents: number | null
  latitude: number | null
  longitude: number | null
  updated_at: string
}

export interface EventListResponse {
  items: EventSummary[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface EventFilters {
  status?: string
  source?: string
  category?: string
  search?: string
  month_key?: string
  page?: number
  page_size?: number
}

export interface EventCreateData {
  name: string
  description?: string
  url?: string
  image_url?: string
  categories?: string[]
  starts_at: string
  ends_at?: string
  venue_name?: string
  city?: string
  state?: string
  price_min_cents?: number
  price_max_cents?: number
}

// Discover Events API
export async function getAdminEvents(filters: EventFilters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.append(key, String(value))
  })
  return fetchWithAuth<EventListResponse>(`/admin/events?${params}`)
}

export async function getAdminEvent(id: string) {
  return fetchWithAuth<EventDetail>(`/admin/events/${id}`)
}

export async function createAdminEvent(data: EventCreateData) {
  return fetchWithAuth<EventDetail>('/admin/events', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateAdminEvent(id: string, data: Partial<EventDetail>) {
  return fetchWithAuth<EventDetail>(`/admin/events/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteAdminEvent(id: string) {
  return fetchWithAuth<{ success: boolean }>(`/admin/events/${id}`, {
    method: 'DELETE',
  })
}

export async function bulkUpdateEventStatus(eventIds: string[], status: string) {
  return fetchWithAuth<{ success: boolean; processed: number; errors: number }>('/admin/events/bulk-status', {
    method: 'POST',
    body: JSON.stringify({ event_ids: eventIds, status }),
  })
}

export async function triggerEventIngestion() {
  return fetchWithAuth<{ job_id: string; message: string }>('/admin/events/trigger-ingestion', {
    method: 'POST',
  })
}

// Dashboard stats types
export interface DashboardStats {
  pending_listings: number
  active_users: number
  open_reports: number
  active_shops: number
}

// Stats API
export async function getDashboardStats() {
  return fetchWithAuth<DashboardStats>('/admin/stats')
}

// ─── Orders ───────────────────────────────────────────────────────────────────

export interface AdminOrderSummary {
  id: string
  created_at: string
  updated_at: string | null
  buyer_id: string
  seller_id: string
  shop_id: string
  status: string
  fulfillment_status: string | null
  delivery_type: string | null
  tracking_status: string | null
  tracking_number: string | null
  carrier: string | null
  currency: string
  total_cents: number | null
  item_count: number
  first_item_name: string | null
  payment_ref: string | null
}

export interface AdminOrderDetail extends AdminOrderSummary {
  items: Record<string, unknown>[]
  shipping_address: Record<string, unknown> | null
  estimated_delivery: string | null
  label_url: string | null
  fee_meta: Record<string, unknown> | null
  buyer_email_snapshot: string | null
}

export interface AdminOrderListResponse {
  orders: AdminOrderSummary[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface AdminOrderFilters {
  status?: string
  fulfillment_status?: string
  shop_id?: string
  buyer_id?: string
  seller_id?: string
  page?: number
  page_size?: number
}

export interface SimulateOrderIn {
  listing_id: string
  buyer_user_id: string
  quantity?: number
  buyer_email?: string
}

export interface SimulateOrderOut {
  order_id: string
  payment_ref: string
  status: string
  fulfillment_status: string
  delivery_type: string | null
  listing_name: string | null
}

export interface SimulateFulfillmentIn {
  fulfillment_status: string
  tracking_number?: string
  carrier?: string
}

export interface SimulateTrackingIn {
  tracking_status: string
}

export async function getAdminOrders(filters: AdminOrderFilters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.append(key, String(value))
  })
  return fetchWithAuth<AdminOrderListResponse>(`/admin/orders?${params}`)
}

export async function getAdminOrder(id: string) {
  return fetchWithAuth<AdminOrderDetail>(`/admin/orders/${id}`)
}

export async function simulateOrder(payload: SimulateOrderIn) {
  return fetchWithAuth<SimulateOrderOut>('/admin/orders/simulate', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function simulateFulfillment(orderId: string, payload: SimulateFulfillmentIn) {
  return fetchWithAuth<{
    order_id: string
    fulfillment_status: string
    status: string
    tracking_status: string | null
    tracking_number: string | null
    carrier: string | null
  }>(`/admin/orders/${orderId}/simulate/fulfillment`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function simulateTracking(orderId: string, payload: SimulateTrackingIn) {
  return fetchWithAuth<{ order_id: string; tracking_status: string; status: string }>(
    `/admin/orders/${orderId}/simulate/tracking`,
    { method: 'POST', body: JSON.stringify(payload) },
  )
}

export interface ReleaseFundsOut {
  order_id: string
  released_payments: number
  amount_cents: number | null
  stripe_transfer_id: string | null
}

export async function releaseOrderFunds(orderId: string, reason: string) {
  return fetchWithAuth<ReleaseFundsOut>(`/admin/orders/${orderId}/release-funds`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

// ── Support Tickets ──

export interface SupportTicketSummary {
  id: string
  user_id: string
  user_email: string | null
  type: string
  subject: string
  status: string
  assigned_to: string | null
  app_version: string | null
  device_os: string | null
  created_at: string
  updated_at: string
}

export interface SupportMessage {
  id: string
  sender_id: string
  sender_type: 'user' | 'admin'
  body: string
  is_internal: boolean
  created_at: string
}

export interface SupportTicketDetail extends SupportTicketSummary {
  messages: SupportMessage[]
}

export interface SupportTicketListResponse {
  tickets: SupportTicketSummary[]
  total: number
  page: number
  page_size: number
}

export async function getSupportTickets(filters: {
  status?: string
  type?: string
  page?: number
  page_size?: number
}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.type) params.set('type', filters.type)
  if (filters.page) params.set('page', String(filters.page))
  if (filters.page_size) params.set('page_size', String(filters.page_size))
  return fetchWithAuth<SupportTicketListResponse>(`/admin/support/tickets?${params}`)
}

export async function getSupportTicket(id: string) {
  return fetchWithAuth<SupportTicketDetail>(`/admin/support/tickets/${id}`)
}

export async function assignSupportTicket(id: string) {
  return fetchWithAuth<SupportTicketDetail>(`/admin/support/tickets/${id}/assign`, { method: 'POST' })
}

export async function updateSupportTicket(id: string, update: { status?: string }) {
  return fetchWithAuth<SupportTicketDetail>(`/admin/support/tickets/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(update),
  })
}

export async function replySupportTicket(ticketId: string, body: string, isInternal: boolean = false) {
  return fetchWithAuth<SupportTicketDetail>(`/admin/support/tickets/${ticketId}/reply`, {
    method: 'POST',
    body: JSON.stringify({ body, is_internal: isInternal }),
  })
}

// ---------------------------------------------------------------------------
// Fee schedules (per-shop negotiated rate overrides)
// ---------------------------------------------------------------------------

export interface FeeScheduleDetail {
  id: string
  shop_id: string
  seller_processing_rate_bps: number | null
  management_commission_bps: number | null
  waive_pickup_marketplace_rate: boolean
  effective_from: string
  effective_until: string | null
  contract_reference: string | null
  notes: string | null
  created_by: string
  created_at: string
}

export interface FeeScheduleHistory {
  shop_id: string
  items: FeeScheduleDetail[]
}

export interface FeeScheduleCreatePayload {
  seller_processing_rate_bps?: number | null
  management_commission_bps?: number | null
  waive_pickup_marketplace_rate?: boolean
  contract_reference?: string | null
  notes?: string | null
}

export async function getActiveFeeSchedule(shopId: string) {
  return fetchWithAuth<FeeScheduleDetail | null>(
    `/admin/shops/${shopId}/fee-schedules/active`,
  )
}

export async function getFeeScheduleHistory(shopId: string) {
  return fetchWithAuth<FeeScheduleHistory>(`/admin/shops/${shopId}/fee-schedules`)
}

export async function createFeeSchedule(
  shopId: string,
  payload: FeeScheduleCreatePayload,
) {
  return fetchWithAuth<FeeScheduleDetail>(`/admin/shops/${shopId}/fee-schedules`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// ---------------------------------------------------------------------------
// On-behalf listing creation (admin lists for a managed shop)
// ---------------------------------------------------------------------------

export interface ManagedListingCreatePayload {
  name: string
  price: number
  description?: string
  category: string
  condition: string
  delivery_type: string
  production_type?: string
  currency?: string
  inventory_count?: number
  materials?: string[]
  tags?: string[]
  brand?: string
  floor_price_cents?: number
}

export interface ManagedListingCreated {
  listing_id: string
  slug_token: string
  shop_id: string
  seller_id: string
  status: string
  created_at: string
}

export async function createListingOnBehalf(
  shopId: string,
  payload: ManagedListingCreatePayload,
) {
  return fetchWithAuth<ManagedListingCreated>(`/admin/shops/${shopId}/listings`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// ---------------------------------------------------------------------------
// Listing variants (admin override — works on any listing)
// ---------------------------------------------------------------------------

export interface ListingOption {
  name: string
  values: string[]
}

export interface ListingVariantPayload {
  option_values: Record<string, string>
  price_cents: number
  qty_available: number
  sku?: string | null
  image_id?: string | null
  weight_oz?: number | null
}

export interface SetVariantsPayload {
  options: ListingOption[]
  variants: ListingVariantPayload[]
}

export interface SetVariantsResult {
  listing_id: string
  variant_count: number
}

export async function setListingVariants(
  listingId: string,
  payload: SetVariantsPayload,
) {
  return fetchWithAuth<SetVariantsResult>(
    `/admin/listings/${listingId}/variants`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  )
}

// ---------------------------------------------------------------------------
// Creator Shop Applications (Early Shops Program — founding 100)
// ---------------------------------------------------------------------------

export type CreatorShopApplicationStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'waitlisted'
  | 'canceled'

export interface CreatorShopApplicationSummary {
  id: string
  applicant_user_id: string
  applicant_email: string | null
  shop_id: string | null
  shop_name: string | null
  desired_handle: string | null
  status: CreatorShopApplicationStatus
  submitted_at: string | null
  decided_at: string | null
  decided_by: string | null
  decision_reason_category: string | null
}

export interface WorkLink {
  url: string
  label?: string | null
  [key: string]: unknown
}

export interface CreatorShopApplicationDetail extends CreatorShopApplicationSummary {
  what_they_make: string | null
  work_links: WorkLink[] | null
  application_reason: string | null
  listing_focus: string | null
  expected_categories: string[] | null
  upcoming_events: string | null
  decision_notes: string | null
  reason: string | null
}

export interface CreatorShopApplicationListResponse {
  items: CreatorShopApplicationSummary[]
  total: number
  page: number
  page_size: number
  total_pages: number
  has_more: boolean
  has_previous: boolean
}

export interface CreatorShopApplicationFilters {
  status?: CreatorShopApplicationStatus | ''
  applicant_user_id?: string
  shop_id?: string
  date_from?: string
  date_to?: string
  page?: number
  page_size?: number
}

export interface CapStatus {
  cap: number
  used: number
  available: number
}

export async function getApplications(filters: CreatorShopApplicationFilters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      params.append(key, String(value))
    }
  })
  return fetchWithAuth<CreatorShopApplicationListResponse>(
    `/admin/creators/applications?${params}`,
  )
}

export async function getApplicationCapStatus() {
  return fetchWithAuth<CapStatus>('/admin/creators/applications/cap-status')
}

export async function getApplication(id: string) {
  return fetchWithAuth<CreatorShopApplicationDetail>(
    `/admin/creators/applications/${id}`,
  )
}

export async function approveApplication(id: string, decisionNotes?: string) {
  return fetchWithAuth<CreatorShopApplicationDetail>(
    `/admin/creators/applications/${id}/approve`,
    {
      method: 'POST',
      body: JSON.stringify({ decision_notes: decisionNotes ?? null }),
    },
  )
}

export async function rejectApplication(
  id: string,
  decisionReasonCategory: string,
  decisionNotes?: string,
) {
  return fetchWithAuth<CreatorShopApplicationDetail>(
    `/admin/creators/applications/${id}/reject`,
    {
      method: 'POST',
      body: JSON.stringify({
        decision_reason_category: decisionReasonCategory,
        decision_notes: decisionNotes ?? null,
      }),
    },
  )
}

export async function moveApplicationWaitlist(
  id: string,
  target: 'waitlisted' | 'pending',
) {
  return fetchWithAuth<CreatorShopApplicationDetail>(
    `/admin/creators/applications/${id}/waitlist`,
    {
      method: 'POST',
      body: JSON.stringify({ target }),
    },
  )
}

// ---------------------------------------------------------------------------
// Shop Plans (Free / Paid Creator)
// ---------------------------------------------------------------------------

export type ShopPlanTier = 'free_creator' | 'paid_creator'
export type ShopPlanStatus = 'active' | 'expired' | 'cancelled'
export type ShopPlanSource =
  | 'early_shops_program'
  | 'paid'
  | 'comp'
  | 'manual_grant'
  | 'auto_downgrade'
  | 'self_signup'

export interface ShopPlanRead {
  id: string
  shop_id: string
  tier: ShopPlanTier
  status: ShopPlanStatus
  source: ShopPlanSource
  started_at: string
  expires_at: string | null
  last_event_at: string
  created_at: string
  updated_at: string
}

export interface ShopPlanGrantPayload {
  tier: ShopPlanTier
  source: ShopPlanSource
  duration_days: number | null
}

export async function getShopPlan(shopId: string) {
  return fetchWithAuth<ShopPlanRead | null>(`/admin/shops/${shopId}/plan`)
}

export async function grantShopPlan(shopId: string, payload: ShopPlanGrantPayload) {
  return fetchWithAuth<ShopPlanRead>(`/admin/shops/${shopId}/plan`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function revokeShopPlan(shopId: string) {
  return fetchWithAuth<ShopPlanRead | null>(`/admin/shops/${shopId}/plan`, {
    method: 'DELETE',
  })
}

// ---------------------------------------------------------------------------
// Shop Badges
// ---------------------------------------------------------------------------

export const BADGE_EARLY_SHOPS_PROGRAM_FOUNDING = 'early_shops_program_founding_member'

export interface ShopBadgeRead {
  id: string
  shop_id: string
  badge_type: string
  metadata: Record<string, unknown>
  earned_at: string
}

export async function listShopBadges(shopId: string, includeRevoked = false) {
  const params = new URLSearchParams()
  if (includeRevoked) params.set('include_revoked', 'true')
  return fetchWithAuth<ShopBadgeRead[]>(
    `/admin/shops/${shopId}/badges${params.toString() ? `?${params}` : ''}`,
  )
}

export async function awardShopBadge(
  shopId: string,
  badgeType: string,
  metadata: Record<string, unknown> = {},
) {
  return fetchWithAuth<ShopBadgeRead>(`/admin/shops/${shopId}/badges`, {
    method: 'POST',
    body: JSON.stringify({ badge_type: badgeType, metadata }),
  })
}

export async function revokeShopBadge(
  shopId: string,
  badgeType: string,
  revokedReason?: string,
) {
  return fetchWithAuth<ShopBadgeRead>(`/admin/shops/${shopId}/badges/revoke`, {
    method: 'POST',
    body: JSON.stringify({
      badge_type: badgeType,
      revoked_reason: revokedReason ?? null,
    }),
  })
}

// ---------------------------------------------------------------------------
// Shop Bans (with frozen snapshot for the public archive)
// ---------------------------------------------------------------------------

export interface ShopBanRead {
  id: string
  shop_id: string
  reason: string
  reason_category: string | null
  moderator_user_id: string
  decided_at: string
  snapshot: Record<string, unknown>
  lifted_at: string | null
  lifted_by: string | null
  lifted_reason: string | null
}

export async function getActiveShopBan(shopId: string) {
  return fetchWithAuth<ShopBanRead | null>(`/admin/shops/${shopId}/ban`)
}

export async function banShop(
  shopId: string,
  reason: string,
  reasonCategory?: string,
) {
  return fetchWithAuth<ShopBanRead>(`/admin/shops/${shopId}/ban`, {
    method: 'POST',
    body: JSON.stringify({
      reason,
      reason_category: reasonCategory ?? null,
    }),
  })
}

export async function liftShopBan(shopId: string, liftedReason: string) {
  return fetchWithAuth<ShopBanRead | null>(`/admin/shops/${shopId}/ban/lift`, {
    method: 'POST',
    body: JSON.stringify({ lifted_reason: liftedReason }),
  })
}

// ----------------------------------------------------------------------------
// Notifications (push test triggers)
// ----------------------------------------------------------------------------

export interface RegisteredDevice {
  id: string
  user_id: string
  user_email: string | null
  device_id: string
  platform: string
  status: string
  token_preview: string
  created_at: string | null
  updated_at: string | null
}

export type PushMode = 'notification' | 'data_only' | 'mixed' | 'auto'
export type PushPriority = 'high' | 'normal'

export interface AdminTestPushIn {
  device_id?: string
  user_id?: string
  title?: string
  body?: string
  data?: Record<string, string>
  deeplink?: string
  category?: string
  mode?: PushMode
  priority?: PushPriority
  content_available?: boolean
}

export interface AdminTestPushResult {
  target: string
  success: boolean
  fcm_message_id: string | null
  error: string | null
}

export interface AdminTestPushResponse {
  sent: number
  results: AdminTestPushResult[]
}

export async function listRegisteredDevices(params: {
  user_id?: string
  search?: string
  limit?: number
} = {}) {
  const qs = new URLSearchParams()
  if (params.user_id) qs.set('user_id', params.user_id)
  if (params.search) qs.set('search', params.search)
  if (params.limit) qs.set('limit', String(params.limit))
  const query = qs.toString() ? `?${qs.toString()}` : ''
  return fetchWithAuth<{ items: RegisteredDevice[]; total: number }>(
    `/admin/notifications/devices${query}`,
  )
}

export async function adminTestPush(payload: AdminTestPushIn) {
  return fetchWithAuth<AdminTestPushResponse>(`/admin/notifications/test-push`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// ────────────────────────────────────────────────────────────
// Programs (Booth Fund and future campaigns)
// ────────────────────────────────────────────────────────────

export type ProgramApplicationStatus = 'pending' | 'approved' | 'rejected' | 'paid'

export interface ProgramApplicationDetail {
  id: string
  program: string
  status: ProgramApplicationStatus
  applicant_email: string
  applicant_name: string
  shop_id: string | null
  payload: Record<string, unknown>
  reviewer_notes: string | null
  utm: Record<string, string> | null
  created_at: string
  reviewed_at: string | null
  reviewed_by: string | null
  shop_handle: string | null
  shop_has_listing: boolean | null
}

export interface ProgramApplicationListResponse {
  items: ProgramApplicationDetail[]
  total: number
}

export interface ProgramApplicationFilters {
  program?: string
  status?: ProgramApplicationStatus | ''
  limit?: number
  offset?: number
}

export async function getProgramApplications(filters: ProgramApplicationFilters = {}) {
  const params = new URLSearchParams()
  if (filters.program) params.set('program', filters.program)
  if (filters.status) params.set('status', filters.status)
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.offset !== undefined) params.set('offset', String(filters.offset))
  const qs = params.toString() ? `?${params.toString()}` : ''
  return fetchWithAuth<ProgramApplicationListResponse>(`/admin/programs/applications${qs}`)
}

export async function getProgramApplication(id: string) {
  return fetchWithAuth<ProgramApplicationDetail>(`/admin/programs/applications/${id}`)
}

export async function updateProgramApplication(
  id: string,
  body: { status?: ProgramApplicationStatus; reviewer_notes?: string }
) {
  return fetchWithAuth<ProgramApplicationDetail>(`/admin/programs/applications/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

// -------------------------------------------------------------------------
// Trust (Sentinel) — moderator surface for trust & safety decisions.
// Backed by /admin/trust/* in barta-api, which proxies to Sentinel.
// -------------------------------------------------------------------------
export type TrustSurface = 'event' | 'text' | 'image'
export type TrustAction = 'allow' | 'review' | 'block'
export type TrustLayer =
  | 'cache'
  | 'rules'
  | 'embeddings'
  | 'llm'
  | 'rekognition'
  | 'disabled'
export type TrustLabelKind =
  | 'confirm'
  | 'false_positive'
  | 'false_negative'
  | 'ambiguous'

export interface TrustReason {
  code: string
  description: string
  score?: number | null
}

export interface TrustDecision {
  decision_id: string
  surface: TrustSurface
  action: TrustAction
  score: number
  layer: TrustLayer
  labels: Record<string, number>
  reasons: TrustReason[]
  rule_version: string
  model_version: string
  latency_ms: number
  created_at: string
}

export interface TrustQueueResponse {
  items: TrustDecision[]
  next_cursor: string | null
}

export interface TrustMetrics {
  block_rate: Record<TrustSurface, number>
  override_rate: number
  l3_escalation_rate: number
  p99_latency_ms: number
}

async function unwrapTrust<T>(res: APIResponse<T>): Promise<T> {
  if (!res.ok || res.data === undefined) {
    throw new Error(res.error?.message || 'Trust API request failed')
  }
  return res.data
}

export async function getTrustQueue(params: {
  surface?: TrustSurface
  cursor?: string
  limit?: number
} = {}): Promise<TrustQueueResponse> {
  const qs = new URLSearchParams()
  if (params.surface) qs.set('surface', params.surface)
  if (params.cursor) qs.set('cursor', params.cursor)
  if (params.limit) qs.set('limit', String(params.limit))
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  return unwrapTrust(await fetchWithAuth<TrustQueueResponse>(`/admin/trust/queue${suffix}`))
}

export async function searchTrustDecisions(filters: {
  input_hash?: string
  surface?: TrustSurface
  action?: TrustAction
  from?: string
  to?: string
  cursor?: string
  limit?: number
} = {}): Promise<TrustQueueResponse> {
  const qs = new URLSearchParams()
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.set(k, String(v))
  })
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  return unwrapTrust(await fetchWithAuth<TrustQueueResponse>(`/admin/trust/decisions${suffix}`))
}

export async function getTrustDecision(decisionId: string): Promise<TrustDecision> {
  return unwrapTrust(await fetchWithAuth<TrustDecision>(`/admin/trust/decisions/${decisionId}`))
}

export async function labelTrustDecision(
  decisionId: string,
  body: { label: TrustLabelKind; category?: string; notes?: string }
): Promise<{ label_id: string }> {
  return unwrapTrust(
    await fetchWithAuth<{ label_id: string }>(`/admin/trust/decisions/${decisionId}/label`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  )
}

export async function getTrustMetrics(): Promise<TrustMetrics> {
  return unwrapTrust(await fetchWithAuth<TrustMetrics>('/admin/trust/metrics'))
}

// Featured Shops types
export interface FeaturedShopSummary {
  id: string
  shop_id: string
  shop_name: string
  shop_handle: string | null
  shop_avatar_url: string | null
  sort_order: number
  is_active: boolean
  note: string | null
  created_at: string
  updated_at: string
}

export interface FeaturedShopDetail extends FeaturedShopSummary {
  created_by: string | null
  shop_bio: string | null
  shop_location_city: string | null
  shop_location_state: string | null
}

export interface FeaturedShopListResponse {
  items: FeaturedShopSummary[]
  total: number
  page: number
  page_size: number
  total_pages: number
  has_more: boolean
  has_previous: boolean
}

export interface FeaturedShopFilters {
  is_active?: string
  search?: string
  page?: number
  page_size?: number
}

export interface FeaturedShopCreateData {
  shop_id: string
  note?: string
  is_active?: boolean
}

export interface FeaturedShopUpdateData {
  note?: string
  is_active?: boolean
  sort_order?: number
}

// Featured Shops API
export async function getFeaturedShops(filters: FeaturedShopFilters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.append(key, String(value))
  })
  return fetchWithAuth<FeaturedShopListResponse>(`/admin/featured-shops?${params}`)
}

export async function createFeaturedShop(data: FeaturedShopCreateData) {
  return fetchWithAuth<FeaturedShopDetail>('/admin/featured-shops', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateFeaturedShop(id: string, data: FeaturedShopUpdateData) {
  return fetchWithAuth<FeaturedShopDetail>(`/admin/featured-shops/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteFeaturedShop(id: string) {
  return fetchWithAuth<{ success: boolean; message: string }>(`/admin/featured-shops/${id}`, {
    method: 'DELETE',
  })
}

export async function reorderFeaturedShops(ids: string[]) {
  return fetchWithAuth<{ success: boolean; message: string }>('/admin/featured-shops/reorder', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  })
}

// Announcements (site-wide banner) types
export type AnnouncementLevel = 'info' | 'warning' | 'critical'

export interface AnnouncementSummary {
  id: string
  message: string
  level: AnnouncementLevel
  link_url: string | null
  link_label: string | null
  dismissible: boolean
  is_active: boolean
  starts_at: string | null
  ends_at: string | null
  created_at: string
  updated_at: string
}

export interface AnnouncementDetail extends AnnouncementSummary {
  created_by: string | null
}

export interface AnnouncementListResponse {
  items: AnnouncementSummary[]
  total: number
  page: number
  page_size: number
  total_pages: number
  has_more: boolean
  has_previous: boolean
}

export interface AnnouncementFilters {
  is_active?: string
  level?: AnnouncementLevel
  search?: string
  page?: number
  page_size?: number
}

export interface AnnouncementCreateData {
  message: string
  level?: AnnouncementLevel
  link_url?: string | null
  link_label?: string | null
  dismissible?: boolean
  is_active?: boolean
  starts_at?: string | null
  ends_at?: string | null
}

export type AnnouncementUpdateData = Partial<AnnouncementCreateData>

// Announcements API
export async function getAnnouncements(filters: AnnouncementFilters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.append(key, String(value))
  })
  return fetchWithAuth<AnnouncementListResponse>(`/admin/announcements?${params}`)
}

export async function createAnnouncement(data: AnnouncementCreateData) {
  return fetchWithAuth<AnnouncementDetail>('/admin/announcements', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateAnnouncement(id: string, data: AnnouncementUpdateData) {
  return fetchWithAuth<AnnouncementDetail>(`/admin/announcements/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteAnnouncement(id: string) {
  return fetchWithAuth<{ success: boolean; message: string }>(`/admin/announcements/${id}`, {
    method: 'DELETE',
  })
}

// Event Cities types
export type EventCityRegion = 'Northeast' | 'Midwest' | 'South' | 'West'

export interface EventCitySummary {
  id: string
  slug: string
  name: string
  state_code: string
  display_name: string | null
  region: EventCityRegion | null
  cover_image_url: string | null
  sort_order: number
  is_active: boolean
  event_count: number
  created_at: string
  updated_at: string
}

export type EventCityDetail = EventCitySummary

export interface EventCityListResponse {
  items: EventCitySummary[]
  total: number
  page: number
  page_size: number
  total_pages: number
  has_more: boolean
  has_previous: boolean
}

export interface EventCityFilters {
  is_active?: string
  region?: string
  search?: string
  page?: number
  page_size?: number
}

export interface EventCityCreateData {
  name: string
  state_code: string
  display_name?: string
  region?: EventCityRegion
  cover_image_url?: string
  is_active?: boolean
}

export interface EventCityUpdateData {
  name?: string
  state_code?: string
  display_name?: string
  region?: EventCityRegion | null
  cover_image_url?: string
  is_active?: boolean
  sort_order?: number
}

// Event Cities API
export async function getEventCities(filters: EventCityFilters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.append(key, String(value))
  })
  return fetchWithAuth<EventCityListResponse>(`/admin/event-cities?${params}`)
}

export async function createEventCity(data: EventCityCreateData) {
  return fetchWithAuth<EventCityDetail>('/admin/event-cities', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateEventCity(id: string, data: EventCityUpdateData) {
  return fetchWithAuth<EventCityDetail>(`/admin/event-cities/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteEventCity(id: string) {
  return fetchWithAuth<{ success: boolean; message: string }>(`/admin/event-cities/${id}`, {
    method: 'DELETE',
  })
}

export async function reorderEventCities(ids: string[]) {
  return fetchWithAuth<{ success: boolean; message: string }>('/admin/event-cities/reorder', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  })
}
